import React from "react";
import {
  AppBar,
  Toolbar,
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LanguageIcon from "@mui/icons-material/Language";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";

const Navbar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [langAnchor, setLangAnchor] = React.useState<null | HTMLElement>(null);

  const navItems = [
    { key: "home", path: "/" },
    { key: "products", path: "/products" },
    { key: "certificates", path: "/certificates" },
    { key: "about", path: "/about" },
    { key: "contact", path: "/contact" },
  ];

  const isActive = (path: string) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  const currentLangLabel = i18n.language?.toLowerCase().startsWith("en")
    ? "EN"
    : "TR";

  return (
    <AppBar
      position="sticky"
      color="transparent"
      elevation={0}
      sx={{
        borderBottom: "1px solid rgba(0,0,0,0.06)",
        backgroundColor: "rgba(255,255,255,0.7)",
      }}
    >
      <Container>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            minHeight: 72,
          }}
        >
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <Box
              component="img"
              src="/brand/yanmazkapi-logo.jpg"
              alt="Yanmaz Kapı"
              sx={{
                height: 40,
                width: 40,
                borderRadius: 1.5,
                objectFit: "cover",
                objectPosition: "center 25%",
                display: "block",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                color: "primary.main",
                fontWeight: 800,
                display: { xs: "none", sm: "block" },
              }}
            >
              Yanmaz Kapı
            </Typography>
          </Box>

          {isMobile ? (
            <Box>
              <Button
                color="primary"
                variant="text"
                startIcon={<LanguageIcon />}
                onClick={(e) => setLangAnchor(e.currentTarget)}
                sx={{ fontWeight: 600 }}
              >
                {currentLangLabel}
              </Button>
              <IconButton color="primary" onClick={() => setMobileOpen(true)}>
                <MenuIcon />
              </IconButton>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {navItems.map((item) => (
                <Button
                  key={item.key}
                  component={RouterLink}
                  to={item.path}
                  color={isActive(item.path) ? "primary" : "inherit"}
                  sx={{ fontWeight: isActive(item.path) ? 700 : 500 }}
                >
                  {t(`nav.${item.key}`)}
                </Button>
              ))}
              <Button
                color="primary"
                variant="text"
                startIcon={<LanguageIcon />}
                onClick={(e) => setLangAnchor(e.currentTarget)}
                sx={{ fontWeight: 700 }}
              >
                {currentLangLabel}
              </Button>
            </Box>
          )}
        </Toolbar>
      </Container>

      <Menu
        anchorEl={langAnchor}
        open={Boolean(langAnchor)}
        onClose={() => setLangAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            i18n.changeLanguage("tr");
            setLangAnchor(null);
          }}
          selected={i18n.language === "tr"}
        >
          Türkçe
        </MenuItem>
        <MenuItem
          onClick={() => {
            i18n.changeLanguage("en");
            setLangAnchor(null);
          }}
          selected={i18n.language === "en"}
        >
          English
        </MenuItem>
      </Menu>

      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
      >
        <Box sx={{ width: 280, p: 2 }}>
          <List>
            {navItems.map((item) => (
              <ListItem key={item.key} disablePadding>
                <ListItemButton
                  component={RouterLink}
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  selected={isActive(item.path)}
                >
                  <ListItemText primary={t(`nav.${item.key}`)} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
